import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Zap,
  Copy,
  Check,
  Eye,
  EyeOff,
  Activity,
  Cpu,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Radio,
  Gauge,
  Users,
  TrendingUp,
  Timer,
  MemoryStick,
  Server,
  Hash,
  CheckCircle2,
  XCircle,
  Layers,
  Wifi,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Ban,
  Fingerprint,
  Network,
  Circle,
  Loader2,
  ScrollText,
  MessageSquare,
  Globe,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { VLLMInstanceDetails, VLLMRequestTrace, mockVLLMInstanceDetails } from '@/mock/gpu-instance';
import { cn } from '@/lib/utils';
import { useTaskLogs, useListSSHInstances } from '@/service/gpu-instance/hook';
import { TaskStage, StageStatus, LogEntry } from '@/service/gpu-instance/type';
import { useDemoMode } from '@/context/DemoModeContext';

const PROXY_BASE_URL = 'https://proxy-instance.float16.cloud';
const DEFAULT_VLLM_PORT = 3900;


// Stage display names
const stageLabels: Record<TaskStage['stage'], string> = {
  docker_pull: 'Docker Pull',
  hf_download: 'HF Download',
  vllm_server: 'vLLM Server',
};

// Get status icon for a stage
const getStageStatusIcon = (status: StageStatus) => {
  switch (status) {
    case 'completed':
      return <Check className="h-4 w-4 text-green-500" />;
    case 'running':
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'pending':
    default:
      return <Circle className="h-4 w-4 text-gray-400" />;
  }
};

// Get stage detail text
const getStageDetail = (stage: TaskStage): string => {
  switch (stage.stage) {
    case 'docker_pull':
      if (stage.elapsed_seconds) {
        return `${stage.elapsed_seconds.toFixed(1)}s`;
      }
      return stage.image || '';
    case 'hf_download':
      if (stage.total_size_human) {
        return stage.total_size_human;
      }
      return stage.model_id || '';
    case 'vllm_server':
      if (stage.api_ready) {
        return 'API Ready';
      }
      return stage.served_model || '';
    default:
      return '';
  }
};

// Get stage container class based on status
const getStageContainerClass = (status: StageStatus): string => {
  switch (status) {
    case 'completed':
      return 'border-green-500/50 bg-green-500/10';
    case 'running':
      return 'border-blue-500/50 bg-blue-500/10';
    case 'failed':
      return 'border-red-500/50 bg-red-500/10';
    case 'pending':
    default:
      return 'border-gray-500/30 bg-gray-500/5';
  }
};

// Format nanosecond timestamp
const formatLogTimestamp = (nanoseconds: string): string => {
  try {
    const ms = BigInt(nanoseconds) / BigInt(1_000_000);
    const date = new Date(Number(ms));
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
};

// Get log level color
const getLogLevelColor = (message: string): string => {
  if (message.includes('[ERROR]')) return 'text-red-400';
  if (message.includes('[WARN]')) return 'text-yellow-400';
  if (message.includes('[DEBUG]')) return 'text-gray-400';
  if (message.includes('[INFO]')) return 'text-green-400';
  return 'text-gray-300';
};

interface VLLMDetailPanelProps {
  taskId: string;
}

const VLLMDetailPanel = ({ taskId }: VLLMDetailPanelProps) => {
  const { isDemoMode } = useDemoMode();
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showChunks, setShowChunks] = useState(true);
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());
  const [requestBodyOpen, setRequestBodyOpen] = useState(true);

  // Combined server state - single source of truth
  const [serverState, setServerState] = useState<{
    isLoading: boolean;
    isReady: boolean;
    modelName: string;
    error: string | null;
  }>({
    isLoading: true,
    isReady: false,
    modelName: '',
    error: null,
  });

  // Refs to prevent duplicate calls
  const isMountedRef = useRef(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get instance to find parent_task_id
  const { data: instances } = useListSSHInstances();
  const instance = instances?.find((i) => i.task_id === taskId);
  const parentTaskId = instance?.parent_task_id || instance?.task_id || taskId;

  // Build proxy endpoints
  const proxyBase = `${PROXY_BASE_URL}/${parentTaskId}/${DEFAULT_VLLM_PORT}`;

  // In a real app, this would be fetched from API
  const details: VLLMInstanceDetails = useMemo(() => mockVLLMInstanceDetails(taskId), [taskId]);

  // Fetch task logs for setup progress - stop when server is ready
  const { data: logsData, isLoading: isLoadingLogs } = useTaskLogs(taskId, {
    limit: 20,
    enabled: !!taskId && !serverState.isReady,
    refetchInterval: serverState.isReady ? false : 5000,
  });

  const stages = logsData?.stages || [];
  const recentLogs = logsData?.logs || [];

  // Check server status - fetches health and model in parallel
  useEffect(() => {
    isMountedRef.current = true;

    const checkServer = async () => {
      // Skip if already ready
      if (serverState.isReady) return;

      try {
        // Fetch health and models in parallel
        const [healthResult, modelsResult] = await Promise.allSettled([
          fetch(`${proxyBase}/health`, {
            method: 'GET',
            signal: AbortSignal.timeout(5000),
          }),
          fetch(`${proxyBase}/v1/models`, {
            method: 'GET',
            signal: AbortSignal.timeout(5000),
          }),
        ]);

        if (!isMountedRef.current) return;

        const isHealthy = healthResult.status === 'fulfilled' && healthResult.value.ok;

        let modelName = '';
        if (modelsResult.status === 'fulfilled' && modelsResult.value.ok) {
          try {
            const data = await modelsResult.value.json();
            const models = data.data || [];
            if (models.length > 0) {
              modelName = models[0].id;
            }
          } catch {
            // Ignore JSON parse errors
          }
        }

        // Single state update with all data
        setServerState({
          isLoading: false,
          isReady: isHealthy,
          modelName,
          error: isHealthy ? null : 'Server not ready',
        });

        // Stop polling if healthy
        if (isHealthy && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } catch {
        if (!isMountedRef.current) return;
        setServerState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Failed to check server status',
        }));
      }
    };

    // Initial check
    checkServer();

    // Poll every 10 seconds if not ready
    intervalRef.current = setInterval(checkServer, 10000);

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [proxyBase, serverState.isReady]);

  // Derived state
  const isServerReady = serverState.isReady;
  const modelName = serverState.modelName;
  const isCheckingServer = serverState.isLoading;

  // Check if all stages are completed
  const allStagesCompleted = stages.length > 0 && stages.every((s) => s.status === 'completed');

  // Sort stages in expected order
  const sortedStages = useMemo(() => {
    const stageOrder: TaskStage['stage'][] = ['docker_pull', 'hf_download', 'vllm_server'];
    return [...stages].sort(
      (a, b) => stageOrder.indexOf(a.stage) - stageOrder.indexOf(b.stage)
    );
  }, [stages]);

  // Navigation helpers
  const basePath = isDemoMode
    ? `/demo/dashboard/${orgId}/${workspaceId}/gpu-instance/${taskId}`
    : `/dashboard/${orgId}/${workspaceId}/gpu-instance/${taskId}`;

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 16) return '••••••••••••••••';
    return `${key.slice(0, 10)}${'•'.repeat(16)}${key.slice(-4)}`;
  };

  const getStatusColor = (status: VLLMInstanceDetails['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500/10 text-green-600 border-green-500/30';
      case 'degraded':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
      case 'offline':
        return 'bg-red-500/10 text-red-600 border-red-500/30';
    }
  };

  const toggleRequestExpanded = (requestId: string) => {
    setExpandedRequests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const { productionMetrics, liveStream } = details;

  return (
    <div className="space-y-6">
      {/* Instance Setup Progress */}
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ScrollText className="h-5 w-5" />
              <CardTitle className="text-base">Instance Setup Progress</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {modelName && (
                <Badge variant="secondary" className="font-mono text-xs">
                  {modelName}
                </Badge>
              )}
              {isCheckingServer ? (
                <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Checking
                </Badge>
              ) : isServerReady ? (
                <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Ready
                </Badge>
              ) : (
                <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Setting up
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isCheckingServer ? (
            /* Loading State */
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Checking server status...</span>
            </div>
          ) : isServerReady ? (
            /* Ready State - Show API Endpoint */
            <div className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground mb-2">API Endpoint</div>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-muted px-2 py-1 rounded flex-1 overflow-x-auto">
                    {proxyBase}/v1/chat/completions
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => handleCopy(`${proxyBase}/v1/chat/completions`, 'endpoint')}
                  >
                    {copiedField === 'endpoint' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* Setting Up State - Show Stages and Logs */
            <>
              {/* Stages Progress */}
              {sortedStages.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {sortedStages.map((stage, index) => (
                    <div key={stage.stage} className="flex items-center">
                      <div
                        className={`flex flex-col gap-1 px-4 py-3 rounded-lg border ${getStageContainerClass(stage.status)}`}
                      >
                        <div className="flex items-center gap-2">
                          {getStageStatusIcon(stage.status)}
                          <span className="font-medium text-sm">
                            {stageLabels[stage.stage]}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground pl-6">
                          {getStageDetail(stage)}
                        </span>
                      </div>
                      {index < sortedStages.length - 1 && (
                        <div className="w-6 h-px bg-border mx-1" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Recent Logs */}
              {recentLogs.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs text-muted-foreground mb-2">Recent Logs</div>
                  <div className="bg-[#1a1b26] rounded-lg p-3 max-h-32 overflow-y-auto font-mono text-xs">
                    {recentLogs.slice(0, 10).map((log, index) => (
                      <div key={index} className="flex gap-2">
                        <span className="text-gray-500 shrink-0">
                          {formatLogTimestamp(log.timestamp)}
                        </span>
                        <span className={getLogLevelColor(log.message)}>
                          {log.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          </CardContent>
        </Card>

      {/* Quick Actions - only when server is ready */}
      {isServerReady && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate(`${basePath}/playground`)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">Playground</div>
                  <div className="text-xs text-muted-foreground">Chat with your model</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate(`${basePath}/endpoint`)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Globe className="h-5 w-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">Endpoint</div>
                  <div className="text-xs text-muted-foreground">API access & examples</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate(`${basePath}/logs`)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <ScrollText className="h-5 w-5 text-orange-500" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">Logs</div>
                  <div className="text-xs text-muted-foreground">View server logs</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}


      {/* Demo Mode Only - Show full vLLM dashboard */}
      {isDemoMode && (
        <>
      {/* Header Card */}
      <Card className="border-yellow-500/50 mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Zap className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  vLLM Inference Server
                  <Badge variant="outline" className={getStatusColor(details.status)}>
                    {details.status}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  High-throughput LLM serving with PagedAttention
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono">
                {details.modelName}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {details.modelSize}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Production Dashboard */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Gauge className="h-5 w-5" />
            Production Dashboard
          </CardTitle>
          <CardDescription>Real-time inference metrics</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Primary Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Activity className="h-4 w-4" />
                <span className="text-xs">Requests/sec</span>
              </div>
              <div className="text-2xl font-bold">{productionMetrics.requestsPerSecond}</div>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-xs">Avg Latency</span>
              </div>
              <div className="text-2xl font-bold">{productionMetrics.avgLatencyMs}ms</div>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Zap className="h-4 w-4" />
                <span className="text-xs">Tokens/sec</span>
              </div>
              <div className="text-2xl font-bold">{productionMetrics.tokensPerSecond}</div>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs">Error Rate</span>
              </div>
              <div className="text-2xl font-bold">{productionMetrics.errorRate}%</div>
            </div>
          </div>

          {/* Latency Percentiles */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-muted/30 rounded-lg text-center">
              <div className="text-xs text-muted-foreground mb-1">P50 Latency</div>
              <div className="text-lg font-semibold">{productionMetrics.p50LatencyMs}ms</div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-center">
              <div className="text-xs text-muted-foreground mb-1">P95 Latency</div>
              <div className="text-lg font-semibold">{productionMetrics.p95LatencyMs}ms</div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-center">
              <div className="text-xs text-muted-foreground mb-1">P99 Latency</div>
              <div className="text-lg font-semibold">{productionMetrics.p99LatencyMs}ms</div>
            </div>
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Cpu className="h-3 w-3" />
                <span className="text-xs">GPU Utilization</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">{productionMetrics.gpuUtilization}%</span>
                <Progress value={productionMetrics.gpuUtilization} className="h-2 flex-1" />
              </div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <MemoryStick className="h-3 w-3" />
                <span className="text-xs">GPU Memory</span>
              </div>
              <div className="text-lg font-semibold">
                {productionMetrics.gpuMemoryUsed}/{productionMetrics.gpuMemoryTotal}GB
              </div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Users className="h-3 w-3" />
                <span className="text-xs">Active Connections</span>
              </div>
              <div className="text-lg font-semibold">{productionMetrics.activeConnections}</div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Timer className="h-3 w-3" />
                <span className="text-xs">Uptime</span>
              </div>
              <div className="text-lg font-semibold">{productionMetrics.uptime}</div>
            </div>
          </div>

          {/* 24h Stats */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="flex items-center justify-between p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">24h Requests</span>
              </div>
              <span className="text-lg font-bold">{formatNumber(productionMetrics.totalRequests24h)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-purple-500/5 rounded-lg border border-purple-500/20">
              <div className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-muted-foreground">24h Tokens</span>
              </div>
              <span className="text-lg font-bold">{formatNumber(productionMetrics.totalTokens24h)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bot Prevention */}
      <Card id="bot-prevention">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Bot Prevention
              </CardTitle>
              <CardDescription>Rate limiting and Proof of Work protection</CardDescription>
            </div>
            <Badge
              variant="outline"
              className={cn(
                "text-xs",
                details.botPrevention.enabled
                  ? "text-green-600 border-green-600/30 bg-green-500/10"
                  : "text-gray-500 border-gray-500/30"
              )}
            >
              {details.botPrevention.enabled ? (
                <><ShieldCheck className="h-3 w-3 mr-1" /> Active</>
              ) : (
                <><ShieldAlert className="h-3 w-3 mr-1" /> Disabled</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Protection Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-red-500/5 rounded-lg border border-red-500/20">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <Ban className="h-3 w-3" />
                <span className="text-xs">Blocked (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(details.botPrevention.stats.blockedRequests24h)}</div>
            </div>
            <div className="p-3 bg-orange-500/5 rounded-lg border border-orange-500/20">
              <div className="flex items-center gap-2 text-orange-600 mb-1">
                <AlertTriangle className="h-3 w-3" />
                <span className="text-xs">Rate Limited (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(details.botPrevention.stats.rateLimitedRequests24h)}</div>
            </div>
            <div className="p-3 bg-green-500/5 rounded-lg border border-green-500/20">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <Fingerprint className="h-3 w-3" />
                <span className="text-xs">PoW Solved (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(details.botPrevention.stats.powChallengesSolved24h)}</div>
            </div>
            <div className="p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <Timer className="h-3 w-3" />
                <span className="text-xs">Avg PoW Time</span>
              </div>
              <div className="text-lg font-bold">{details.botPrevention.stats.avgPowSolveTimeMs}ms</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Rate Limiting */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Network className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">Rate Limiting by IP</span>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    details.botPrevention.rateLimit.enabled
                      ? "text-green-600 border-green-600/30"
                      : "text-gray-500 border-gray-500/30"
                  )}
                >
                  {details.botPrevention.rateLimit.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Per Minute</span>
                  <span className="font-mono">{details.botPrevention.rateLimit.requestsPerMinute}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Per Hour</span>
                  <span className="font-mono">{details.botPrevention.rateLimit.requestsPerHour}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Burst Limit</span>
                  <span className="font-mono">{details.botPrevention.rateLimit.burstLimit}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Blocked IPs</span>
                  <span className="font-mono">{details.botPrevention.rateLimit.blockedIPs.length}</span>
                </div>
              </div>
              {details.botPrevention.rateLimit.whitelistedIPs.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Whitelisted:</span>{' '}
                  {details.botPrevention.rateLimit.whitelistedIPs.join(', ')}
                </div>
              )}
            </div>

            {/* PoW Stats Summary */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Fingerprint className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">Proof of Work Stats</span>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    details.botPrevention.proofOfWork.enabled
                      ? "text-green-600 border-green-600/30"
                      : "text-gray-500 border-gray-500/30"
                  )}
                >
                  {details.botPrevention.proofOfWork.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Solved (24h)</span>
                  <span className="font-mono">{formatNumber(details.botPrevention.stats.powChallengesSolved24h)}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Avg Solve Time</span>
                  <span className="font-mono">{details.botPrevention.stats.avgPowSolveTimeMs}ms</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                See PoW Protected Endpoint section below for integration details.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PoW Protected Endpoint */}
      {details.botPrevention.proofOfWork.enabled && (
        <Card className="border-purple-500/30 mt-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Fingerprint className="h-5 w-5 text-purple-500" />
                  PoW Protected Endpoint
                </CardTitle>
                <CardDescription>
                  Requires solving a SHA256 challenge before making requests
                </CardDescription>
              </div>
              <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
                Challenge Required
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* PoW Configuration */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Algorithm</div>
                <div className="font-mono text-sm uppercase">{details.botPrevention.proofOfWork.algorithm}</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Difficulty</div>
                <Badge variant="secondary" className="text-xs capitalize">
                  {details.botPrevention.proofOfWork.difficulty}
                </Badge>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Challenge Validity</div>
                <div className="font-mono text-sm">{details.botPrevention.proofOfWork.validitySeconds}s</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Skip Auth Users</div>
                <div className="font-mono text-sm">{details.botPrevention.proofOfWork.exemptAuthenticatedUsers ? 'Yes' : 'No'}</div>
              </div>
            </div>

            {/* Challenge Flow */}
            <div className="space-y-3">
              <div className="text-sm font-medium">Integration Flow</div>

              {/* Step 1: Get Challenge */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-blue-600 text-white text-xs font-semibold">Step 1</span>
                  <span className="text-sm font-medium">Request Challenge</span>
                </div>
                <div className="relative">
                  <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`GET ${details.botPrevention.proofOfWork.challengeEndpoint}

Response:
{
  "challenge": "a]f8e2...",
  "difficulty": 4,
  "expires_at": 1704067200
}`}
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-1 right-1 h-7 text-gray-400 hover:text-white"
                    onClick={() => handleCopy(`GET ${details.botPrevention.proofOfWork.challengeEndpoint}`, 'pow-step1')}
                  >
                    {copiedField === 'pow-step1' ? <Check className="size-3" /> : <Copy className="size-3" />}
                  </Button>
                </div>
              </div>

              {/* Step 2: Solve Challenge */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-amber-500 text-white text-xs font-semibold">Step 2</span>
                  <span className="text-sm font-medium">Solve Challenge (Client-side)</span>
                </div>
                <div className="relative">
                  <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`// Find nonce where SHA256(challenge + nonce) starts with N zeros
let nonce = 0;
while (true) {
  const hash = SHA256(challenge + nonce);
  if (hash.startsWith("0".repeat(difficulty))) {
    break; // Solution found
  }
  nonce++;
}`}
                  </pre>
                </div>
              </div>

              {/* Step 3: Make Request */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-emerald-600 text-white text-xs font-semibold">Step 3</span>
                  <span className="text-sm font-medium">Make API Request with Solution</span>
                </div>
                <div className="relative">
                  <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`POST ${details.botPrevention.proofOfWork.protectedEndpoint}
Headers:
  X-PoW-Challenge: <challenge>
  X-PoW-Nonce: <nonce>
  Authorization: Bearer ${details.apiKey}

Body:
{
  "model": "${details.modelName}",
  "messages": [{"role": "user", "content": "Hello!"}],
  "stream": true
}`}
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-1 right-1 h-7 text-gray-400 hover:text-white"
                    onClick={() => handleCopy(
                      `curl -X POST ${details.botPrevention.proofOfWork.protectedEndpoint} -H "X-PoW-Challenge: <challenge>" -H "X-PoW-Nonce: <nonce>" -H "Authorization: Bearer ${details.apiKey}" -H "Content-Type: application/json" -d '{"model": "${details.modelName}", "messages": [{"role": "user", "content": "Hello!"}], "stream": true}'`,
                      'pow-step3'
                    )}
                  >
                    {copiedField === 'pow-step3' ? <Check className="size-3" /> : <Copy className="size-3" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Endpoints Summary */}
            <div className="pt-4 border-t space-y-2">
              <div className="text-xs font-medium text-muted-foreground">Endpoints</div>
              <div className="grid gap-2">
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">GET</Badge>
                    <span className="text-muted-foreground">Challenge</span>
                  </div>
                  <code className="font-mono text-xs">{details.botPrevention.proofOfWork.challengeEndpoint}</code>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">POST</Badge>
                    <span className="text-muted-foreground">Protected API</span>
                  </div>
                  <code className="font-mono text-xs">{details.botPrevention.proofOfWork.protectedEndpoint}</code>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Streaming Analytics Terminal */}
      <div className="bg-[#1a1b26] rounded-lg border border-gray-800 overflow-hidden">
        {/* Terminal Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-[#16171f]">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <span className="text-gray-300 text-sm font-medium">Streaming Analytics</span>
          </div>
          <Badge className={cn(
            "text-xs font-semibold",
            liveStream.isActive
              ? "bg-green-500 hover:bg-green-500 text-white"
              : "bg-gray-600 hover:bg-gray-600 text-gray-300"
          )}>
            {liveStream.isActive ? (
              <><Radio className="h-3 w-3 mr-1 animate-pulse" /> LIVE</>
            ) : (
              'IDLE'
            )}
          </Badge>
        </div>

        {/* Terminal Content */}
        <div className="p-4 space-y-4">
          {/* POST Endpoint Row */}
          <div className="flex items-center gap-3">
            <Badge className="bg-green-600 hover:bg-green-600 text-white text-xs font-mono">POST</Badge>
            <code className="text-gray-300 text-sm font-mono">/v1/chat/completions</code>
            <div className="flex items-center gap-2 ml-auto">
              <Badge variant="outline" className="text-gray-400 border-gray-700 text-xs">
                {details.modelName}
              </Badge>
              <Badge variant="outline" className="text-cyan-400 border-cyan-700/50 text-xs">
                stream: true
              </Badge>
            </div>
          </div>

          {/* Request Body (Collapsible) */}
          {liveStream.currentRequest && (
            <Collapsible open={requestBodyOpen} onOpenChange={setRequestBodyOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wide hover:text-gray-300 transition-colors">
                {requestBodyOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Request Body
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <pre className="bg-[#13141c] rounded-lg p-3 text-xs font-mono text-gray-300 overflow-x-auto">
{JSON.stringify({
  model: liveStream.currentRequest.model,
  messages: liveStream.currentRequest.messages,
  stream: liveStream.currentRequest.stream,
  max_tokens: liveStream.currentRequest.maxTokens,
}, null, 2)}
                </pre>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Stream Statistics */}
          {liveStream.streamingStats && (
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-2">Stream Statistics</div>
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-[#13141c] rounded-lg p-3 text-center">
                  <div className="text-gray-500 text-xs mb-1">Total Chunks</div>
                  <div className="text-white text-lg font-bold">{liveStream.streamingStats.totalChunks}</div>
                </div>
                <div className="bg-[#13141c] rounded-lg p-3 text-center">
                  <div className="text-gray-500 text-xs mb-1">Total Tokens</div>
                  <div className="text-white text-lg font-bold">{liveStream.streamingStats.totalTokens}</div>
                </div>
                <div className="bg-[#13141c] rounded-lg p-3 text-center">
                  <div className="text-gray-500 text-xs mb-1">Tokens/sec</div>
                  <div className="text-cyan-400 text-lg font-bold">{liveStream.streamingStats.tokensPerSecond}</div>
                </div>
                <div className="bg-[#13141c] rounded-lg p-3 text-center">
                  <div className="text-gray-500 text-xs mb-1">Total Duration</div>
                  <div className="text-white text-lg font-bold">{liveStream.streamingStats.totalDuration}</div>
                </div>
              </div>
            </div>
          )}

          {/* SSE Connection Status */}
          <div className="flex items-center gap-2 text-sm">
            <Wifi className="h-4 w-4 text-green-500" />
            <span className="text-gray-400">SSE Connection:</span>
            <Badge variant="outline" className={cn(
              "text-xs",
              liveStream.streamingStats?.status === 'streaming'
                ? "text-cyan-400 border-cyan-700/50"
                : liveStream.streamingStats?.status === 'completed'
                ? "text-green-400 border-green-700/50"
                : "text-gray-400 border-gray-700"
            )}>
              {liveStream.streamingStats?.status || 'idle'}
            </Badge>
            {liveStream.streamingStats && (
              <span className="text-gray-500 text-xs ml-2">
                TTFT: {liveStream.streamingStats.timeToFirstToken}ms
              </span>
            )}
          </div>

          {/* Concatenated Response */}
          {liveStream.chunks.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-gray-400 text-xs uppercase tracking-wide">Concatenated Response</div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowChunks(true)}
                    className={cn(
                      "h-6 px-2 text-xs",
                      showChunks ? "bg-gray-700 text-white" : "text-gray-500 hover:text-gray-300"
                    )}
                  >
                    Show Chunks
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowChunks(false)}
                    className={cn(
                      "h-6 px-2 text-xs",
                      !showChunks ? "bg-gray-700 text-white" : "text-gray-500 hover:text-gray-300"
                    )}
                  >
                    Raw Text
                  </Button>
                </div>
              </div>
              <div className="bg-[#13141c] rounded-lg p-3 font-mono text-sm text-gray-200 leading-relaxed max-h-48 overflow-y-auto">
                {showChunks ? (
                  liveStream.chunks.map((chunk) => (
                    <span key={chunk.index}>
                      <span className="text-cyan-500 text-xs">[{chunk.index}]</span>
                      {chunk.text}
                    </span>
                  ))
                ) : (
                  liveStream.chunks.map((chunk) => chunk.text).join('')
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Request Tracing */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Request Tracing
          </CardTitle>
          <CardDescription>Recent inference requests with detailed metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Latency</TableHead>
                <TableHead>Tokens</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {details.recentRequests.map((request: VLLMRequestTrace) => (
                <>
                  <TableRow
                    key={request.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleRequestExpanded(request.id)}
                  >
                    <TableCell>
                      {expandedRequests.has(request.id) ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {formatTimestamp(request.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-600 hover:bg-green-600 text-white text-[10px] px-1.5 py-0">
                          {request.method}
                        </Badge>
                        <code className="text-xs text-muted-foreground">{request.endpoint}</code>
                      </div>
                    </TableCell>
                    <TableCell>
                      {request.status === 'success' ? (
                        <Badge variant="outline" className="text-green-600 border-green-600/30 text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Success
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-600 border-red-600/30 text-xs">
                          <XCircle className="h-3 w-3 mr-1" />
                          Error
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{request.latencyMs}ms</TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {request.inputTokens} → {request.outputTokens}
                      </span>
                    </TableCell>
                  </TableRow>
                  {expandedRequests.has(request.id) && (
                    <TableRow key={`${request.id}-expanded`}>
                      <TableCell colSpan={6} className="bg-muted/30 p-4">
                        <div className="space-y-4">
                          {/* Request Messages */}
                          <div>
                            <div className="text-xs font-medium text-muted-foreground mb-2">Request Messages</div>
                            <pre className="bg-muted rounded-lg p-3 text-xs font-mono overflow-x-auto">
{JSON.stringify(request.messages, null, 2)}
                            </pre>
                          </div>
                          {/* Response */}
                          <div>
                            <div className="text-xs font-medium text-muted-foreground mb-2">Response</div>
                            <div className="bg-muted rounded-lg p-3 text-sm">{request.response}</div>
                          </div>
                          {/* Streaming Stats */}
                          {request.streamingStats && (
                            <div>
                              <div className="text-xs font-medium text-muted-foreground mb-2">Streaming Stats</div>
                              <div className="grid grid-cols-5 gap-2">
                                <div className="bg-muted rounded-lg p-2 text-center">
                                  <div className="text-xs text-muted-foreground">Chunks</div>
                                  <div className="font-semibold">{request.streamingStats.totalChunks}</div>
                                </div>
                                <div className="bg-muted rounded-lg p-2 text-center">
                                  <div className="text-xs text-muted-foreground">Tokens</div>
                                  <div className="font-semibold">{request.streamingStats.totalTokens}</div>
                                </div>
                                <div className="bg-muted rounded-lg p-2 text-center">
                                  <div className="text-xs text-muted-foreground">Tok/sec</div>
                                  <div className="font-semibold">{request.streamingStats.tokensPerSecond}</div>
                                </div>
                                <div className="bg-muted rounded-lg p-2 text-center">
                                  <div className="text-xs text-muted-foreground">Duration</div>
                                  <div className="font-semibold">{request.streamingStats.totalDuration}</div>
                                </div>
                                <div className="bg-muted rounded-lg p-2 text-center">
                                  <div className="text-xs text-muted-foreground">TTFT</div>
                                  <div className="font-semibold">{request.streamingStats.timeToFirstToken}ms</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* API Access Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Server className="h-5 w-5" />
            API Access
          </CardTitle>
          <CardDescription>OpenAI-compatible API endpoint</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Endpoint</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap break-all pr-12 font-mono">
                {details.endpoint}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() => handleCopy(details.endpoint, 'endpoint')}
              >
                {copiedField === 'endpoint' ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">API Key</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap break-all pr-20 font-mono">
                {showApiKey ? details.apiKey : maskApiKey(details.apiKey)}
              </pre>
              <div className="absolute top-1 right-1 flex gap-1">
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => handleCopy(details.apiKey, 'apiKey')}>
                  {copiedField === 'apiKey' ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Example Request */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Example Request</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs whitespace-pre-wrap break-all pr-12 overflow-x-auto font-mono">
{`curl ${details.endpoint} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${details.apiKey}" \\
  -d '{
    "model": "${details.modelName}",
    "messages": [{"role": "user", "content": "Hello!"}],
    "stream": true,
    "max_tokens": 512
  }'`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() =>
                  handleCopy(
                    `curl ${details.endpoint} -H "Content-Type: application/json" -H "Authorization: Bearer ${details.apiKey}" -d '{"model": "${details.modelName}", "messages": [{"role": "user", "content": "Hello!"}], "stream": true, "max_tokens": 512}'`,
                    'curl'
                  )
                }
              >
                {copiedField === 'curl' ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          {/* Model Configuration Summary */}
          <div className="pt-4 border-t">
            <div className="text-xs font-medium text-muted-foreground mb-3">Model Configuration</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div className="flex justify-between p-2 bg-muted/50 rounded">
                <span className="text-muted-foreground">Context</span>
                <span className="font-mono">{(details.contextLength / 1000).toFixed(0)}K</span>
              </div>
              <div className="flex justify-between p-2 bg-muted/50 rounded">
                <span className="text-muted-foreground">Max Output</span>
                <span className="font-mono">{details.maxTokens}</span>
              </div>
              <div className="flex justify-between p-2 bg-muted/50 rounded">
                <span className="text-muted-foreground">Dtype</span>
                <span className="font-mono">{details.config.dtype}</span>
              </div>
              <div className="flex justify-between p-2 bg-muted/50 rounded">
                <span className="text-muted-foreground">TP</span>
                <span className="font-mono">{details.config.tensorParallel}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> This is simulated data. In production,
          metrics will be real-time and the API endpoint will be fully functional.
        </AlertDescription>
      </Alert>
        </>
      )}
    </div>
  );
};

export default VLLMDetailPanel;
